import { ChartMarker, chartSeriesStyle, cn, useTranslate, type ChartSeriesStyle } from '@athena/ui'
import { useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent } from 'react'

import { useFormat } from '../../lib/format.js'
import type { ChartModel, ChartPoint } from './progress-model.js'

export interface SubjectProgressChartProps {
  model: ChartModel
  /** Already localized. What the chart shows, for assistive technology. */
  label: string
  /** Where the hint "scroll sideways" is, so the plot can point at it. */
  describedBy?: string
}

/*
 * ── Geometry, in real pixels ────────────────────────────────────────────
 *
 * The chart used to be one fixed viewBox scaled to its card, so every label
 * shrank with the card: 22px text at 1440 was 8px at 390. It is laid out in
 * CSS pixels now, from the width it is actually given. Text is text-sized at
 * every width, markers stay about 10px, and only the spacing between points
 * adapts — until it cannot shrink any further, and then the plot scrolls
 * instead of compressing.
 */

/** The score labels' column, outside the scroller, so 0–100% stays in view while it scrolls. */
const AXIS_WIDTH = 44
const PAD_X = 16
const PAD_TOP = 12
/** The band under the plot for day labels and day ticks. */
const AXIS_BAND = 32
/**
 * The narrowest gap between two attempts. Wide enough that ten-pixel markers
 * never touch and a fingertip can tell neighbours apart; below it the plot
 * widens and scrolls, newest end in view.
 */
const MIN_STEP = 28
/** The narrowest spacing of one calendar day on the daily view's time axis. */
const MIN_DAY = 18
/** The narrowest gap between two axis labels, so they never overprint. */
const LABEL_GAP = 76
/** How near a pointer must be to a point for that point to be the one it means. */
const HIT_RADIUS = 24
const TOOLTIP_GAP = 14
/** Used until the chart has been measured, and in test environments that never measure. */
const FALLBACK_WIDTH = 640

/** Shorter on a phone, but never so short the trend flattens: 200px of plot at the least. */
function heightFor(width: number): number {
  return width < 480 ? 232 : 280
}

interface Positioned {
  point: ChartPoint
  style: ChartSeriesStyle
  cx: number
  cy: number
}

interface Layout {
  width: number
  height: number
  y: (percent: number) => number
  positioned: Positioned[]
  labels: { x: number; text: string; anchor: 'start' | 'middle' | 'end' }[]
  /** Where one calendar day of attempts ends and the next begins. */
  boundaries: number[]
}

/** The reader's own calendar day, which is the day an attempt's label names. */
function localDay(iso: string): string {
  const at = new Date(iso)
  return `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`
}

/**
 * Labels that fit: each at least `LABEL_GAP` from the last, the outermost two
 * hugging the plot's edges rather than hanging off them. Everything a label
 * is dropped for is still in the tooltip and the table.
 */
function thin(candidates: { x: number; text: string }[], width: number): Layout['labels'] {
  const labels: Layout['labels'] = []
  for (const candidate of candidates) {
    const previous = labels.at(-1)
    if (previous && candidate.x - previous.x < LABEL_GAP) continue
    labels.push({ ...candidate, anchor: 'middle' })
  }
  const first = labels[0]
  if (first && first.x - PAD_X < LABEL_GAP / 2) Object.assign(first, { x: PAD_X, anchor: 'start' })
  const last = labels.at(-1)
  if (last && last !== first && width - PAD_X - last.x < LABEL_GAP / 2)
    Object.assign(last, { x: width - PAD_X, anchor: 'end' })
  return labels
}

function layoutFor(
  model: ChartModel,
  available: number,
  format: { shortDate: (iso: string) => string; utcShortDay: (iso: string) => string },
): Layout {
  const height = heightFor(available)
  const plotBottom = height - AXIS_BAND
  const y = (percent: number) => PAD_TOP + (1 - percent / 100) * (plotBottom - PAD_TOP)
  const room = Math.max(available - AXIS_WIDTH, 200)
  const style = (point: ChartPoint) => chartSeriesStyle(point.styleIndex)

  if (model.view === 'attempts') {
    const count = model.points.length
    const step = count > 1 ? Math.max(MIN_STEP, (room - PAD_X * 2) / (count - 1)) : 0
    const width = count > 1 ? PAD_X * 2 + step * (count - 1) : room
    const x = (index: number) => (count > 1 ? PAD_X + index * step : width / 2)

    const positioned = model.points.map((point) => ({
      point,
      style: style(point),
      cx: x(point.kind === 'attempt' ? point.index : 0),
      cy: y(point.percent),
    }))

    // Runs of consecutive attempts on one calendar day: a label under each
    // run that fits, and a tick where a day ends.
    const days: { from: number; to: number; iso: string }[] = []
    model.points.forEach((point, index) => {
      if (point.kind !== 'attempt') return
      const current = days.at(-1)
      if (current && localDay(current.iso) === localDay(point.submittedAt)) current.to = index
      else days.push({ from: index, to: index, iso: point.submittedAt })
    })
    const labels = thin(
      days.map((day) => ({ x: (x(day.from) + x(day.to)) / 2, text: format.shortDate(day.iso) })),
      width,
    )
    const boundaries = days.slice(1).map((day, index) => (x(days[index]!.to) + x(day.from)) / 2)
    return { width, height, y, positioned, labels, boundaries }
  }

  // Daily: a real time axis, a day being what each point is.
  const times = model.points.map((point) => (point.kind === 'day' ? Date.parse(point.date) : 0))
  const first = Math.min(...times)
  const last = Math.max(...times)
  const spanDays = (last - first) / 86_400_000
  const perDay = spanDays > 0 ? Math.max(MIN_DAY, (room - PAD_X * 2) / spanDays) : 0
  const width = spanDays > 0 ? PAD_X * 2 + perDay * spanDays : room
  const x = (time: number) =>
    spanDays > 0 ? PAD_X + ((time - first) / 86_400_000) * perDay : width / 2

  const positioned = model.points.map((point, index) => ({
    point,
    style: style(point),
    cx: x(times[index]!),
    cy: y(point.percent),
  }))
  const distinct = [...new Set(times)].sort((a, b) => a - b)
  const labels = thin(
    distinct.map((time) => ({
      x: x(time),
      text: format.utcShortDay(new Date(time).toISOString()),
    })),
    width,
  )
  return { width, height, y, positioned, labels, boundaries: [] }
}

/**
 * Which point the pointer means, and where the hover indicators are drawn.
 * `anchor` outlives `key` while the indicators fade out; `glide` is true only
 * when the pointer went from one point straight to another.
 */
interface Hover {
  key: string | null
  anchor: string | null
  glide: boolean
}

const AT_REST: Hover = { key: null, anchor: null, glide: false }

/**
 * The tutor's chart of one student's scores over time.
 *
 * Hand-drawn SVG rather than a charting library, for the reasons it always
 * was: four polylines do not need a dependency with its own tokens, focus
 * ring and idea of a tooltip. What each point is, and which points a line may
 * join, is decided in `progress-model.ts`; this draws it.
 *
 * ── Encoding ─────────────────────────────────────────────────────────────
 *
 * Each subject has a colour from the chart palette, a dash and a marker
 * shape (`chartSeriesStyle`), keyed to the subject rather than its position
 * after filtering. The score axis is pinned to 0–100%: a percentage has a
 * real floor and ceiling, and a fitted axis turns 78, 80, 82 into a climb.
 *
 * ── Hover and tap ────────────────────────────────────────────────────────
 *
 * The nearest point within 24px of the pointer is the one described — a
 * finger never has to land on a marker. It gets a dotted guide, a soft halo
 * and a tooltip naming its subject, practice, attempt, score and date. A tap
 * keeps it up until the next tap elsewhere; scrolling the plot puts it away.
 * The indicators only change opacity and transform, on the theme's
 * transition duration, which is zero under `prefers-reduced-motion`.
 *
 * ── Keyboard and screen readers ──────────────────────────────────────────
 *
 * The SVG is one `role="img"` with a label saying whose scores these are, in
 * which view, how many and on what scale. Its markers are not Tab stops: a
 * hundred of them would be a hundred stops. Every value, with everything the
 * tooltip says, is in the table view beside it, which is how a keyboard or a
 * screen reader reads the numbers. The tooltip is `aria-hidden` for that
 * reason — it would only repeat a row of the table.
 */
export function SubjectProgressChart({ model, label, describedBy }: SubjectProgressChartProps) {
  const t = useTranslate()
  const format = useFormat()
  const [hover, setHover] = useState<Hover>(AT_REST)
  const [available, setAvailable] = useState(FALLBACK_WIDTH)
  const haloId = `chart-halo-${useId().replace(/[^\w-]/g, '')}`
  const frameRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  // Measured, and re-measured when the card changes width — a rotated phone,
  // a resized window — so the geometry follows and the type does not.
  useLayoutEffect(() => {
    const frame = frameRef.current
    if (!frame) return
    const measure = () => {
      if (frame.clientWidth > 0) setAvailable(frame.clientWidth)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(frame)
    return () => observer.disconnect()
  }, [])

  const layout = layoutFor(model, available, format)
  const { width, height, y, positioned } = layout
  const scrollable = width > available - AXIS_WIDTH + 1
  const byKey = new Map(positioned.map((entry) => [entry.point.key, entry]))

  const point = (key: string | null) =>
    setHover((previous) => {
      if (previous.key === key) return previous
      if (key === null) return { ...previous, key: null, glide: false }
      return { key, anchor: key, glide: previous.key !== null }
    })

  // A tap elsewhere on the page puts the tooltip away; a mouse leaving does it too.
  const activeKey = hover.key
  useEffect(() => {
    if (activeKey === null) return
    const dismiss = (event: globalThis.PointerEvent) => {
      if (!svgRef.current?.contains(event.target as Node)) point(null)
    }
    document.addEventListener('pointerdown', dismiss)
    return () => document.removeEventListener('pointerdown', dismiss)
  }, [activeKey])

  // A plot too dense for its card opens on its newest end: the latest attempts
  // are what a tutor came to see, and the older ones are a scroll away.
  useLayoutEffect(() => {
    const scroller = scrollRef.current
    if (scroller && scrollable) scroller.scrollLeft = scroller.scrollWidth
  }, [scrollable, width])

  // Placed after layout, from the point's position on screen — its x less
  // however far the plot is scrolled — and kept inside the chart's frame, so
  // it never renders past the edge of the card or the screen. Beside the
  // point, not above it: two subjects' points can share an x.
  useLayoutEffect(() => {
    const frame = frameRef.current
    const tooltip = tooltipRef.current
    const target = hover.anchor ? byKey.get(hover.anchor) : undefined
    if (!frame || !tooltip || !target) return
    const scrollLeft = scrollRef.current?.scrollLeft ?? 0
    const frameWidth = frame.clientWidth || available
    const px = AXIS_WIDTH + target.cx - scrollLeft
    const right = px + TOOLTIP_GAP
    const side =
      right + tooltip.offsetWidth <= frameWidth ? right : px - TOOLTIP_GAP - tooltip.offsetWidth
    const left = Math.min(Math.max(side, 0), Math.max(frameWidth - tooltip.offsetWidth, 0))
    const top = Math.min(
      Math.max(target.cy - tooltip.offsetHeight / 2, 0),
      Math.max(height - tooltip.offsetHeight, 0),
    )
    tooltip.style.transform = `translate(${left}px, ${top}px)`
  })

  const anchor = hover.anchor ? (byKey.get(hover.anchor) ?? null) : null
  const open = activeKey !== null && anchor !== null
  const state = open ? 'open' : 'closed'
  const shown = open ? 'opacity-100' : 'opacity-0'
  const motion = hover.glide ? 'transition-[opacity,transform]' : 'transition-opacity'
  const at = anchor ?? { cx: 0, cy: 0 }

  const nearest = (event: PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    const px = event.clientX - box.left
    const py = event.clientY - box.top
    let best: Positioned | null = null
    let bestDistance = HIT_RADIUS
    for (const candidate of positioned) {
      const distance = Math.hypot(candidate.cx - px, candidate.cy - py)
      if (distance <= bestDistance) {
        best = candidate
        bestDistance = distance
      }
    }
    return best
  }
  const track = (event: PointerEvent<SVGSVGElement>) => point(nearest(event)?.point.key ?? null)

  return (
    <div ref={frameRef} className="relative min-w-0">
      <div className="flex">
        {/* The score scale, outside the scroller: it stays beside the plot
            however far back a tutor scrolls. */}
        <div aria-hidden="true" className="relative shrink-0" style={{ width: AXIS_WIDTH, height }}>
          {[100, 50, 0].map((value) => (
            <span
              key={value}
              className="absolute right-2 -translate-y-1/2 text-caption tabular-nums text-text-muted"
              style={{ top: y(value) }}
            >
              {t('progressChart.percent', [value])}
            </span>
          ))}
        </div>

        <div
          ref={scrollRef}
          // Its own horizontal scroll, contained so a sideways swipe at the
          // end of the plot does not become a browser back gesture; vertical
          // scrolling passes through to the page untouched.
          className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain"
          onScroll={() => point(null)}
        >
          <svg
            ref={svgRef}
            width={width}
            height={height}
            onPointerMove={track}
            onPointerDown={track}
            onPointerLeave={(event) => {
              // A finger lifting fires this too, and a tapped point stays up.
              if (event.pointerType === 'mouse') point(null)
            }}
            role="img"
            aria-label={label}
            {...(describedBy ? { 'aria-describedby': describedBy } : {})}
            data-view={model.view}
            className="block"
          >
            <defs>
              <filter id={haloId} x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation={3} />
              </filter>
            </defs>

            {/* Hairline grid, solid: dashes belong to the series. */}
            {[0, 50, 100].map((value) => (
              <line
                key={value}
                data-testid="chart-gridline"
                data-value={value}
                x1={0}
                x2={width}
                y1={y(value)}
                y2={y(value)}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
            ))}

            {layout.boundaries.map((boundary) => (
              <line
                key={boundary}
                data-testid="chart-day-boundary"
                x1={boundary}
                x2={boundary}
                y1={y(0) + 4}
                y2={y(0) + 10}
                stroke="var(--color-border-strong)"
                strokeWidth={1}
              />
            ))}

            <g
              data-testid="chart-guide"
              data-state={state}
              className={cn(motion, shown)}
              style={{ transform: `translateX(${at.cx}px)` }}
            >
              <line
                x1={0}
                x2={0}
                y1={y(100)}
                y2={y(0)}
                stroke="var(--color-text-muted)"
                strokeOpacity={0.6}
                strokeWidth={1}
                strokeDasharray="3 4"
                strokeLinecap="round"
              />
            </g>

            <g
              data-testid="chart-halo"
              data-state={state}
              className={cn(motion, shown)}
              style={{ transform: `translate(${at.cx}px, ${at.cy}px)` }}
            >
              {anchor ? (
                <circle
                  r={11}
                  fill={anchor.style.color}
                  fillOpacity={0.3}
                  filter={`url(#${haloId})`}
                />
              ) : null}
            </g>

            {/* Lines only where the model says a sequence is comparable:
                retakes of one practice, or one subject's daily means. */}
            {model.lines.map((line) => {
              const style = chartSeriesStyle(line.styleIndex)
              const points = line.pointKeys.flatMap((key) => {
                const entry = byKey.get(key)
                return entry ? [`${entry.cx},${entry.cy}`] : []
              })
              return (
                <polyline
                  key={line.key}
                  data-testid="chart-line"
                  data-subject={line.subjectId}
                  points={points.join(' ')}
                  fill="none"
                  stroke={style.color}
                  strokeWidth={2}
                  strokeDasharray={style.dash}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              )
            })}

            {positioned.map((entry) => (
              <g
                key={entry.point.key}
                data-testid="chart-point"
                data-subject={entry.point.subjectId}
              >
                <ChartMarker
                  shape={entry.style.shape}
                  cx={entry.cx}
                  cy={entry.cy}
                  fill={entry.style.color}
                />
              </g>
            ))}

            {layout.labels.map((axisLabel) => (
              <text
                key={`${axisLabel.x}-${axisLabel.text}`}
                data-testid="chart-axis-label"
                x={axisLabel.x}
                y={height - 10}
                textAnchor={axisLabel.anchor}
                className="fill-text-muted text-caption"
              >
                {axisLabel.text}
              </text>
            ))}

            <g
              data-testid="chart-highlight"
              data-state={state}
              className={cn(motion, shown)}
              style={{ transform: `translate(${at.cx}px, ${at.cy}px)` }}
            >
              {anchor ? (
                <ChartMarker shape={anchor.style.shape} cx={0} cy={0} fill={anchor.style.color} />
              ) : null}
            </g>
          </svg>
        </div>
      </div>

      <div
        ref={tooltipRef}
        aria-hidden="true"
        data-testid="chart-tooltip"
        data-state={state}
        className={cn(
          'pointer-events-none absolute top-0 left-0 z-10',
          hover.glide && 'transition-transform',
        )}
      >
        <div
          className={cn(
            'flex w-max max-w-60 flex-col gap-0.5 rounded-md bg-text px-3 py-2 text-text-inverse shadow-elevation-2 transition-[opacity,translate]',
            open ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0',
          )}
        >
          {anchor ? <TooltipBody entry={anchor} /> : null}
        </div>
      </div>
    </div>
  )
}

/** What the tooltip says about a point: exactly what it is, attempt or daily mean. */
function TooltipBody({ entry }: { entry: Positioned }) {
  const t = useTranslate()
  const format = useFormat()
  const { point, style } = entry
  const subject = (
    <span className="flex items-center gap-1.5 text-caption">
      <svg viewBox="0 0 14 14" aria-hidden="true" className="size-3.5 shrink-0">
        <ChartMarker shape={style.shape} cx={7} cy={7} fill={style.color} />
      </svg>
      <span className="min-w-0 break-words">{point.subjectName}</span>
    </span>
  )

  if (point.kind === 'attempt') {
    return (
      <>
        {subject}
        <span className="break-words text-label font-semibold">{point.practiceTitle}</span>
        <span className="text-caption">
          {t('progressChart.attemptNumber', [point.attemptNumber])} ·{' '}
          <span className="font-semibold tabular-nums">
            {t('progressChart.percent', [point.percent])}
          </span>
        </span>
        <span className="text-caption opacity-80">{format.dateTime(point.submittedAt)}</span>
      </>
    )
  }
  return (
    <>
      {subject}
      <span className="text-label font-semibold">{t('progressChart.dailyAverage')}</span>
      <span className="text-caption">
        <span className="font-semibold tabular-nums">
          {t('progressChart.percent', [point.percent])}
        </span>{' '}
        · {dailyScope(t, point.attemptCount, point.practiceCount)}
      </span>
      <span className="text-caption opacity-80">{format.utcDay(point.date)}</span>
    </>
  )
}

/** "3 attempts across 2 practices": what a daily mean is a mean of. */
export function dailyScope(
  t: ReturnType<typeof useTranslate>,
  attempts: number,
  practices: number,
): string {
  return t('progressChart.dailyScope', [
    attempts === 1 ? t('progressChart.attemptsOne') : t('progressChart.attemptsOther', [attempts]),
    practices === 1
      ? t('progressChart.practicesOne')
      : t('progressChart.practicesOther', [practices]),
  ])
}
